import React, { useState, useEffect, useRef } from 'react';
import { useResponsive } from '../hooks/useResponsive';
const API = process.env.REACT_APP_API_URL || 'https://lacasadelpuro-pos-production.up.railway.app';
const GOLD = '#C9A84C'; const BORDER = '#2A2A2A'; const TEXT = '#E8E0D0'; const GREEN = '#4CAF50';

const TIER_COLORS = { charter: '#FFD700', founding: '#C9A84C', reserve: '#A0A0A0', corporate: '#4A90D9' };

const TIER_ORDER = ['charter', 'founding', 'reserve', 'corporate'];
const TIER_META = {
  charter:   { capacity: 15,   closed: true,  initFee: '$0',      label: 'CHARTER MEMBERS' },
  founding:  { capacity: 35,   closed: false, initFee: '$150',    label: 'FOUNDING MEMBERS' },
  reserve:   { capacity: null, closed: false, initFee: '$300',    label: 'RESERVE MEMBERS' },
  corporate: { capacity: 7,    closed: false, initFee: '$1,800',  label: 'CORPORATE ACCOUNTS' },
};

const tierKey = (t) => (t || '').toLowerCase();

export default function Members({ staff, hideHeader }) {
  const { isMobile } = useResponsive();
  const [members, setMembers] = useState([]);
  const [corporateAccounts, setCorporateAccounts] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [newMember, setNewMember] = useState({ name:'', email:'', phone:'', tier:'reserve' });
  const [creditAdj, setCreditAdj] = useState('');
  const [creditNote, setCreditNote] = useState('');
  const [showCardModal, setShowCardModal] = useState(false);
  const [cardLoading, setCardLoading] = useState(false);
  const [cardError, setCardError] = useState('');
  const [publishableKey, setPublishableKey] = useState(null);
  const detailRef = useRef(null);
  const stripeRef = useRef(null);
  const cardElementRef = useRef(null);

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (selected && detailRef.current) {
      detailRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selected]);

  useEffect(() => {
    if (!showCardModal || !publishableKey) return;
    if (typeof window.Stripe === 'undefined') {
      setCardError('Stripe.js failed to load');
      return;
    }
    if (!stripeRef.current) {
      stripeRef.current = window.Stripe(publishableKey);
    }
    const elements = stripeRef.current.elements();
    const cardElement = elements.create('card', {
      style: {
        base: {
          color: TEXT,
          fontFamily: 'Georgia, serif',
          fontSize: '14px',
          '::placeholder': { color: '#666' },
        },
        invalid: { color: '#e74c3c' },
      },
    });
    cardElement.mount('#card-element');
    cardElementRef.current = cardElement;
    return () => {
      if (cardElementRef.current) {
        cardElementRef.current.unmount();
        cardElementRef.current = null;
      }
    };
  }, [showCardModal, publishableKey]);

  const load = async () => {
    setLoading(true);
    try {
      const [membersData, corpData] = await Promise.all([
        fetch(API + '/members?includePlaceholders=visible').then(r => r.json()).catch(() => []),
        fetch(API + '/corporate').then(r => r.ok ? r.json() : []).catch(() => []),
      ]);
      setMembers(Array.isArray(membersData) ? membersData : (membersData.members || []));
      setCorporateAccounts(Array.isArray(corpData) ? corpData : []);
    } catch(e) {}
    setLoading(false);
  };

  const loadMember = async (id) => {
    try {
      const res = await fetch(`${API}/members/${id}`);
      if (!res.ok) { console.error('loadMember HTTP', res.status); return; }
      const data = await res.json();
      setSelected(data);
    } catch (e) {
      console.error('loadMember error', e);
    }
  };

  const loadCorporate = async (id) => {
    try {
      const data = await fetch(`${API}/corporate/${id}`).then(r => r.json());
      setSelected({ ...data, _isCorporate: true });
    } catch(e) {}
  };

  const adjustCredit = async () => {
    if (!creditAdj || !selected) return;
    try {
      await fetch(`${API}/credits/adjust`, { method: 'POST', headers: {'Content-Type':'application/json'},
        body: JSON.stringify({ member_id: selected.id, amount: parseFloat(creditAdj), note: creditNote, staff_id: staff.id }) });
      setMsg('Credit adjusted'); setCreditAdj(''); setCreditNote('');
      loadMember(selected.id);
      setTimeout(() => setMsg(''), 2000);
    } catch(e) { setMsg('Error'); }
  };

  const addMember = async () => {
    try {
      await fetch(`${API}/members`, { method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify(newMember) });
      setMsg('Member added'); setShowAdd(false); setNewMember({ name:'', email:'', phone:'', tier:'reserve' });
      load(); setTimeout(() => setMsg(''), 2000);
    } catch(e) { setMsg('Error'); }
  };

  const openCardModal = async () => {
    setCardError('');
    if (!publishableKey) {
      try {
        const res = await fetch(`${API}/stripe/config`);
        const data = await res.json();
        if (!res.ok || !data.publishableKey) {
          setMsg('Stripe not configured');
          setTimeout(() => setMsg(''), 2500);
          return;
        }
        setPublishableKey(data.publishableKey);
      } catch (e) {
        setMsg('Could not load Stripe config');
        setTimeout(() => setMsg(''), 2500);
        return;
      }
    }
    setShowCardModal(true);
  };

  const closeCardModal = () => {
    setShowCardModal(false);
    setCardError('');
    setCardLoading(false);
  };

  const saveCard = async () => {
    if (!selected || !cardElementRef.current || !stripeRef.current) return;
    const memberId = selected.id;
    const memberName = selected.name || '';
    setCardLoading(true);
    setCardError('');
    try {
      const siRes = await fetch(`${API}/stripe/setup-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ member_id: memberId }),
      });
      const siData = await siRes.json();
      if (!siRes.ok || !siData.client_secret) {
        throw new Error(siData.error || 'Could not start card save');
      }

      const confirmResult = await stripeRef.current.confirmCardSetup(siData.client_secret, {
        payment_method: {
          card: cardElementRef.current,
          billing_details: { name: memberName },
        },
      });
      if (confirmResult.error) {
        throw new Error(confirmResult.error.message || 'Card was not accepted');
      }

      // Poll member record until setup_intent.succeeded webhook writes
      // card_on_file=true. Up to 5s total, 500ms intervals.
      const deadline = Date.now() + 5000;
      let settled = false;
      while (Date.now() < deadline) {
        try {
          const mr = await fetch(`${API}/members/${memberId}`);
          const m = await mr.json();
          if (m && m.card_on_file) {
            setSelected(m);
            settled = true;
            break;
          }
        } catch {}
        await new Promise(r => setTimeout(r, 500));
      }
      if (!settled) loadMember(memberId);
      closeCardModal();
      setMsg('Card saved');
      setTimeout(() => setMsg(''), 2000);
    } catch (err) {
      setCardError(err.message || 'Could not save card');
      setCardLoading(false);
    }
  };

  const searchLower = search.toLowerCase();
  const matchesSearch = (text) => !search || (text || '').toLowerCase().includes(searchLower);

  const filteredMembers = members.filter(m => {
    if (!search) return true;
    if (m.is_placeholder) return false;
    return matchesSearch(m.name) || matchesSearch(m.email) || (m.phone || '').includes(search);
  });
  const filteredCorporate = corporateAccounts.filter(c => {
    if (!search) return true;
    return matchesSearch(c.company_name) || matchesSearch(c.contact_name) || matchesSearch(c.contact_email);
  });

  const sortTier = (arr) => [
    ...arr.filter(m => !m.is_placeholder),
    ...arr.filter(m => m.is_placeholder),
  ];

  const grouped = {
    charter:   sortTier(filteredMembers.filter(m => tierKey(m.tier) === 'charter')),
    founding:  sortTier(filteredMembers.filter(m => tierKey(m.tier) === 'founding')),
    reserve:   sortTier(filteredMembers.filter(m => tierKey(m.tier) === 'reserve')),
    corporate: filteredCorporate,
  };

  const inp = { width:'100%', background:'#111', border:`1px solid ${BORDER}`, borderRadius:8, padding:'10px 12px', color:TEXT, fontSize:13, outline:'none', marginBottom:12, boxSizing:'border-box' };
  const lbl = { color:'#888', fontSize:10, letterSpacing:1, textTransform:'uppercase', display:'block', marginBottom:4 };
  const btn = (c) => ({ background:c||'#2A2A2A', border:'none', borderRadius:8, color:c?'#000':TEXT, padding:'8px 16px', cursor:'pointer', fontSize:12, fontFamily:'Georgia' });

  const renderMemberCard = (m) => (
    <div key={m.id} onClick={() => loadMember(m.id)} style={{
      padding:'12px 14px', borderRadius:8, cursor:'pointer', marginBottom:4,
      background: selected?.id === m.id && !selected?._isCorporate ? '#2A2A2A' : '#1A1A1A',
      border: `1px solid ${selected?.id === m.id && !selected?._isCorporate ? GOLD : BORDER}`,
    }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div style={{ fontSize:13, fontWeight:selected?.id===m.id && !selected?._isCorporate ?700:400 }}>{m.name}</div>
        <span style={{ fontSize:9, padding:'2px 6px', borderRadius:4, background:'rgba(201,168,76,0.15)', color:TIER_COLORS[tierKey(m.tier)]||GOLD, border:`1px solid ${TIER_COLORS[tierKey(m.tier)]||GOLD}33` }}>{(m.tier||'').toUpperCase()}</span>
      </div>
      <div style={{ fontSize:11, color:'#666', marginTop:2 }}>{m.phone || m.email || '—'}</div>
      <div style={{ fontSize:11, color:GREEN, marginTop:2 }}>${parseFloat(m.credit_balance||0).toFixed(2)} credit</div>
    </div>
  );

  const renderPlaceholderTile = (m, tier) => (
    <div key={m.id} style={{
      padding:'12px 14px', borderRadius:8, marginBottom:4, cursor:'default',
      background:'#161616', border:'1px solid #222',
    }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div style={{ fontSize:14, fontWeight:500, color:'#444' }}>Open Slot</div>
        <span style={{ fontSize:9, padding:'2px 6px', borderRadius:4, background:'#1a1a1a', color:'#444', border:'1px solid #2a2a2a' }}>{(m.tier||tier).toUpperCase()}</span>
      </div>
      {TIER_META[tier]?.initFee && (
        <div style={{ marginTop:6 }}>
          <span style={{ display:'inline-block', fontSize:11, color:'#555', background:'#111', borderRadius:10, padding:'2px 8px' }}>From {TIER_META[tier].initFee}</span>
        </div>
      )}
    </div>
  );

  const renderCorporateTile = (acct) => {
    const isSelected = selected?._isCorporate && selected?.id === acct.id;
    const empCount = acct.employees?.length ?? acct.individuals?.length ?? acct.employee_count ?? null;
    return (
      <div key={acct.id} onClick={() => loadCorporate(acct.id)} style={{
        padding:'12px 14px', borderRadius:8, cursor:'pointer', marginBottom:4,
        background: isSelected ? '#2A2A2A' : '#1A1A1A',
        border: `1px solid ${isSelected ? GOLD : BORDER}`,
      }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ fontSize:13, fontWeight: isSelected ? 700 : 400 }}>{acct.company_name}</div>
          <span style={{ fontSize:9, padding:'2px 6px', borderRadius:4, background:'rgba(74,144,217,0.15)', color:TIER_COLORS.corporate, border:`1px solid ${TIER_COLORS.corporate}33` }}>CORPORATE</span>
        </div>
        <div style={{ fontSize:11, color:'#888', marginTop:2 }}>{empCount != null ? `${empCount} member${empCount === 1 ? '' : 's'}` : (acct.contact_name || '—')}</div>
        <div style={{ fontSize:11, color:GREEN, marginTop:2 }}>${parseFloat(acct.credit_balance||0).toFixed(2)} credit</div>
      </div>
    );
  };

  const renderSectionHeader = (tier, isFirst) => {
    const meta = TIER_META[tier];
    const items = grouped[tier];
    const realCount = tier === 'corporate' ? items.length : items.filter(m => !m.is_placeholder).length;
    const countLabel = meta.capacity != null ? `${realCount} / ${meta.capacity}` : `${realCount}`;
    const openCount = meta.capacity != null ? Math.max(0, meta.capacity - realCount) : null;
    return (
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop: isFirst ? 0 : 24, marginBottom:8 }}>
        <div style={{ display:'flex', alignItems:'center' }}>
          <span style={{ color:GOLD, fontSize:11, fontWeight:700, letterSpacing:1.5, textTransform:'uppercase' }}>{meta.label}</span>
          {meta.closed && (
            <span style={{ marginLeft:8, border:`1px solid ${GOLD}`, color:GOLD, background:'transparent', fontSize:10, padding:'2px 8px', borderRadius:10, letterSpacing:1 }}>CLOSED</span>
          )}
          {tier === 'founding' && openCount != null && (
            <span style={{ marginLeft:8, color:'#555', fontSize:11 }}>· {openCount} open</span>
          )}
        </div>
        <span style={{ background:'#1a1a1a', border:'1px solid #2a2a2a', color:'#888', fontSize:11, padding:'2px 10px', borderRadius:12 }}>{countLabel}</span>
      </div>
    );
  };

  const renderCorporateDetail = (acct) => {
    const individuals = acct.individuals || acct.employees || [];
    const maxInd = acct.max_individuals || 5;
    return (
      <div style={{ background:'#1A1A1A', border:`1px solid ${BORDER}`, borderRadius:12, padding:24, position:'relative' }}>
        <button onClick={() => setSelected(null)} aria-label="Close detail" style={{ position:'absolute', top:8, right:8, width:44, height:44, display:'flex', alignItems:'center', justifyContent:'center', background:'transparent', border:'none', borderRadius:8, color:'#888', fontSize:26, lineHeight:1, cursor:'pointer', padding:0 }}>×</button>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20, paddingRight:44 }}>
          <div>
            <div style={{ color:GOLD, fontSize:11, letterSpacing:2, textTransform:'uppercase', marginBottom:4 }}>Corporate</div>
            <div style={{ color:TEXT, fontSize:20, fontFamily:'Georgia' }}>{acct.company_name}</div>
            <div style={{ color:'#888', fontSize:13, marginTop:4 }}>{acct.contact_name || '—'} · {acct.contact_email || acct.contact_phone || ''}</div>
          </div>
          <div style={{ textAlign:'right' }}>
            <div style={{ color:'#888', fontSize:10, letterSpacing:1, textTransform:'uppercase' }}>Available Credit</div>
            <div style={{ color:GREEN, fontSize:24, fontFamily:'Georgia' }}>${parseFloat(acct.credit_balance||0).toFixed(2)}</div>
          </div>
        </div>
        <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr', gap:12, marginBottom:20 }}>
          {[['Locker', '—'], ['Guest Passes', acct.guest_passes_remaining ?? acct.guest_passes ?? '—'], ['Member Since', acct.created_at ? new Date(acct.created_at).toLocaleDateString() : '—']].map(([k,v]) => (
            <div key={k} style={{ background:'#111', borderRadius:8, padding:'10px 14px', border:`1px solid ${BORDER}` }}>
              <div style={{ color:'#888', fontSize:10, letterSpacing:1, textTransform:'uppercase', marginBottom:4 }}>{k}</div>
              <div style={{ color:TEXT, fontSize:14 }}>{v}</div>
            </div>
          ))}
        </div>
        <div style={{ background:'#111', borderRadius:8, padding:16, border:`1px solid ${BORDER}`, marginBottom:16 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
            <div style={{ color:'#888', fontSize:11, letterSpacing:1, textTransform:'uppercase' }}>Authorized Individuals</div>
            <div style={{ color:'#555', fontSize:11 }}>({individuals.length} of {maxInd} slots)</div>
          </div>
          {individuals.length === 0 ? (
            <div style={{ color:'#555', fontSize:13 }}>No individuals enrolled</div>
          ) : (
            individuals.map((ind, i) => {
              const active = ind.active !== false;
              return (
                <div key={ind.id ?? ind.member_id ?? i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 0', borderBottom: i === individuals.length - 1 ? 'none' : '1px solid #1a1a1a' }}>
                  <div style={{ color:TEXT, fontSize:13 }}>{ind.name || '—'}</div>
                  <span style={{ fontSize:10, padding:'2px 8px', borderRadius:10, background: active ? '#0D2010' : '#1a1a1a', color: active ? '#97C459' : '#555', letterSpacing:1, textTransform:'uppercase' }}>{active ? 'Active' : 'Inactive'}</span>
                </div>
              );
            })
          )}
        </div>
        <div style={{ background:'#111', borderRadius:8, padding:16, border:`1px solid ${BORDER}`, marginBottom:16 }}>
          <div style={{ color:'#888', fontSize:10, letterSpacing:1, textTransform:'uppercase', marginBottom:12 }}>Adjust Credit</div>
          <div style={{ display:'flex', flexDirection: isMobile ? 'column' : 'row', gap:8 }}>
            <input type="number" disabled placeholder="Amount (+ or -)"
              style={{...inp, marginBottom:0, flex: isMobile ? 'unset' : 1, opacity:0.5}} />
            <input disabled placeholder="Note"
              style={{...inp, marginBottom:0, flex: isMobile ? 'unset' : 2, opacity:0.5}} />
            <button disabled style={{...btn(GOLD), width: isMobile ? '100%' : 'auto', opacity:0.4, cursor:'not-allowed'}}>Apply</button>
          </div>
          <div style={{ color:'#555', fontSize:11, marginTop:10, fontStyle:'italic' }}>Credit management coming soon</div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding:20, color:TEXT, fontFamily:'Georgia' }}>
      {!hideHeader && <h1 style={{ color:GOLD, marginBottom:20, fontSize:22, fontWeight:400 }}>Members</h1>}
      {msg && <div style={{ background:'#1A3A1A', border:'1px solid #4CAF50', borderRadius:8, padding:'10px 16px', marginBottom:16, color:'#4CAF50', fontSize:13 }}>{msg}</div>}
      <div style={{ display:'flex', gap:8, marginBottom:16 }}>
        <div style={{ position:'relative', flex:1 }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search members..." style={{...inp, marginBottom:0, paddingRight:40}} />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              aria-label="Clear search"
              style={{
                position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'transparent', border: 'none', borderRadius: 6,
                color: '#C9A84C', fontSize: 20, lineHeight: 1, cursor: 'pointer', padding: 0,
              }}
            >×</button>
          )}
        </div>
        <button style={btn(GOLD)} onClick={() => setShowAdd(true)}>+</button>
      </div>
      {loading ? <div style={{ color:'#888', padding:20, textAlign:'center' }}>Loading...</div> : (
        <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr 1fr', gap:16, alignItems:'start', marginBottom:24 }}>
          {TIER_ORDER.map((tier) => {
            const items = grouped[tier];
            return (
              <div key={tier} style={{ background:'#111', borderRadius:12, padding:12 }}>
                {renderSectionHeader(tier, true)}
                {tier === 'corporate' && items.length === 0 ? (
                  <div style={{ color:'#555', fontSize:12, textAlign:'center', padding:'20px 0', fontStyle:'italic' }}>
                    No accounts yet
                  </div>
                ) : items.map(item => {
                  if (tier === 'corporate') return renderCorporateTile(item);
                  if (item.is_placeholder) return renderPlaceholderTile(item, tier);
                  return renderMemberCard(item);
                })}
              </div>
            );
          })}
        </div>
      )}
      {selected && (
        <div ref={detailRef} style={{ marginTop:8 }}>
          {selected._isCorporate ? renderCorporateDetail(selected) : (
            <div style={{ background:'#1A1A1A', border:`1px solid ${BORDER}`, borderRadius:12, padding:24, position:'relative' }}>
              <button onClick={() => setSelected(null)} aria-label="Close detail" style={{ position:'absolute', top:8, right:8, width:44, height:44, display:'flex', alignItems:'center', justifyContent:'center', background:'transparent', border:'none', borderRadius:8, color:'#888', fontSize:26, lineHeight:1, cursor:'pointer', padding:0 }}>×</button>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20, paddingRight:44 }}>
                <div>
                  <div style={{ color:GOLD, fontSize:11, letterSpacing:2, textTransform:'uppercase', marginBottom:4 }}>{selected.tier}</div>
                  <div style={{ color:TEXT, fontSize:20, fontFamily:'Georgia' }}>{selected.name}</div>
                  <div style={{ color:'#888', fontSize:13, marginTop:4 }}>{selected.email} · {selected.phone}</div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <div style={{ color:'#888', fontSize:10, letterSpacing:1, textTransform:'uppercase' }}>Available Credit</div>
                  <div style={{ color:GREEN, fontSize:24, fontFamily:'Georgia' }}>${parseFloat(selected.credit_balance||0).toFixed(2)}</div>
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr', gap:12, marginBottom:20 }}>
                {[['Locker', selected.locker_number||'—'], ['Guest Passes', selected.guest_passes_remaining||0], ['Member Since', selected.created_at ? new Date(selected.created_at).toLocaleDateString() : '—']].map(([k,v]) => (
                  <div key={k} style={{ background:'#111', borderRadius:8, padding:'10px 14px', border:`1px solid ${BORDER}` }}>
                    <div style={{ color:'#888', fontSize:10, letterSpacing:1, textTransform:'uppercase', marginBottom:4 }}>{k}</div>
                    <div style={{ color:TEXT, fontSize:14 }}>{v}</div>
                  </div>
                ))}
              </div>
              <div style={{ background:'#111', borderRadius:8, padding:16, border:`1px solid ${BORDER}`, marginBottom:16 }}>
                <div style={{ color:'#888', fontSize:10, letterSpacing:1, textTransform:'uppercase', marginBottom:12 }}>Card on File</div>
                {selected.card_on_file && selected.card_last4 ? (
                  <div style={{ display:'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent:'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap:8 }}>
                    <div style={{ color:TEXT, fontSize:14 }}>
                      {(selected.card_brand || 'Card').charAt(0).toUpperCase() + (selected.card_brand || 'Card').slice(1)} •••• {selected.card_last4}
                      <span style={{ color:'#888', marginLeft:12 }}>Exp {String(selected.card_exp_month || '').padStart(2,'0')}/{selected.card_exp_year || ''}</span>
                    </div>
                    <button style={{...btn(), width: isMobile ? '100%' : 'auto'}} onClick={openCardModal}>Update Card</button>
                  </div>
                ) : (
                  <div style={{ display:'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent:'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap:8 }}>
                    <div style={{ color:'#888', fontSize:13, fontStyle:'italic' }}>No card saved</div>
                    <button style={{...btn(GOLD), width: isMobile ? '100%' : 'auto'}} onClick={openCardModal}>Add Card</button>
                  </div>
                )}
              </div>
              <div style={{ background:'#111', borderRadius:8, padding:16, border:`1px solid ${BORDER}`, marginBottom:16 }}>
                <div style={{ color:'#888', fontSize:10, letterSpacing:1, textTransform:'uppercase', marginBottom:12 }}>Adjust Credit</div>
                <div style={{ display:'flex', flexDirection: isMobile ? 'column' : 'row', gap:8 }}>
                  <input type="number" value={creditAdj} onChange={e => setCreditAdj(e.target.value)} placeholder="Amount (+ or -)"
                    style={{...inp, marginBottom:0, flex: isMobile ? 'unset' : 1}} />
                  <input value={creditNote} onChange={e => setCreditNote(e.target.value)} placeholder="Note"
                    style={{...inp, marginBottom:0, flex: isMobile ? 'unset' : 2}} />
                  <button style={{...btn(GOLD), width: isMobile ? '100%' : 'auto'}} onClick={adjustCredit}>Apply</button>
                </div>
              </div>
              {selected.notes && <div style={{ color:'#888', fontSize:12, fontStyle:'italic', padding:'10px 14px', background:'#111', borderRadius:8, border:`1px solid ${BORDER}` }}>{selected.notes}</div>}
            </div>
          )}
        </div>
      )}
      {showAdd && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:500, padding:16 }} onClick={() => setShowAdd(false)}>
          <div style={{ background:'#1A1A1A', border:`1px solid ${BORDER}`, borderRadius:12, padding:24, width:'100%', maxWidth:440 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ color:TEXT, marginBottom:20, fontWeight:400 }}>Add Member</h3>
            {[['Name','name','text'],['Email','email','email'],['Phone','phone','tel']].map(([l,k,t]) => (
              <div key={k}><label style={lbl}>{l}</label><input type={t} value={newMember[k]} onChange={e => setNewMember({...newMember,[k]:e.target.value})} style={inp} /></div>
            ))}
            <label style={lbl}>Tier</label>
            <select value={newMember.tier} onChange={e => setNewMember({...newMember,tier:e.target.value})} style={{...inp}}>
              {['charter','founding','reserve','corporate'].map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>)}
            </select>
            <div style={{ display:'flex', gap:8 }}>
              <button style={{...btn(GOLD),flex:1,padding:'12px 0',fontSize:14}} onClick={addMember}>Add Member</button>
              <button style={{...btn(),flex:1,padding:'12px 0',fontSize:14}} onClick={() => setShowAdd(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {showCardModal && selected && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:500, padding:16 }} onClick={() => !cardLoading && closeCardModal()}>
          <div style={{ background:'#1A1A1A', border:`1px solid ${BORDER}`, borderRadius:12, padding:24, width:'100%', maxWidth:440 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ color:TEXT, marginBottom:20, fontWeight:400 }}>{selected.card_on_file ? 'Update Card on File' : 'Add Card on File'}</h3>
            <div style={{ color:'#888', fontSize:12, marginBottom:12 }}>Member: {selected.name}</div>
            <div id="card-element" style={{ background:'#111', border:`1px solid ${BORDER}`, borderRadius:8, padding:'12px 14px', marginBottom:16, minHeight:40 }} />
            {cardError && <div style={{ color:'#e74c3c', fontSize:12, marginBottom:12 }}>{cardError}</div>}
            <div style={{ display:'flex', gap:8 }}>
              <button style={{...btn(GOLD),flex:1,padding:'12px 0',fontSize:14, opacity: cardLoading ? 0.5 : 1, cursor: cardLoading ? 'wait' : 'pointer'}} onClick={saveCard} disabled={cardLoading}>{cardLoading ? 'Saving…' : 'Save Card'}</button>
              <button style={{...btn(),flex:1,padding:'12px 0',fontSize:14}} onClick={closeCardModal} disabled={cardLoading}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
